import PropTypes from 'prop-types';
import { Grid, Card, CardContent, Typography, CircularProgress, Box } from '@mui/material';

/**
 * Reusable statistics cards component for displaying key metrics
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.statistics - Array of statistic objects
 * @param {boolean} props.loading - Loading state
 * @param {number} props.columns - Number of columns for grid layout
 *
 * @example
 * <StatisticsCards
 *   statistics={[
 *     { label: 'Total Users', value: 150, color: 'primary.main' },
 *     { label: 'Active', value: 120, color: 'success.main' },
 *     { label: 'Pending', value: 30, color: 'warning.main' }
 *   ]}
 *   loading={false}
 *   columns={3}
 * />
 */
const StatisticsCards = ({ statistics, loading = false, columns = 4 }) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 3
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!statistics || statistics.length === 0) {
    return null;
  }

  const gridSize = 12 / columns;

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {statistics.map((stat, index) => (
        <Grid key={index} size={{ xs: 12, sm: 6, md: gridSize }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography
                gutterBottom
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  color: stat.color || 'text.primary',
                  fontWeight: 700
                }}
              >
                {stat.value !== undefined && stat.value !== null ? stat.value : '-'}
              </Typography>
              <Box sx={{ minHeight: '20px', mt: 0.5 }}>
                {stat.subtitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary'
                    }}
                  >
                    {stat.subtitle}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

StatisticsCards.propTypes = {
  statistics: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      color: PropTypes.string,
      subtitle: PropTypes.string
    })
  ).isRequired,
  loading: PropTypes.bool,
  columns: PropTypes.number
};

export default StatisticsCards;
